import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Role } from '../../../../core/models/models';
import { RoleService } from '../../../../core/services/role.service';
import { ConfirmationDialogComponent } from '../../../../shared/components/dialogs/confirmation-dialog.component';

@Component({
    selector: 'app-role-dialog',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatIconModule, ConfirmationDialogComponent],
    templateUrl: './role-dialog.component.html',
    styleUrl: './role-dialog.component.scss'
})
export class RoleDialogComponent implements OnInit {
    roleForm: FormGroup;
    errorMessage: string = '';
    isDeleteConfirmOpen = false;
    canDeleteRole = false;

    constructor(
        private fb: FormBuilder,
        private roleService: RoleService,
        private cdr: ChangeDetectorRef,
        private dialogRef: MatDialogRef<RoleDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: Role
    ) {
        this.roleForm = this.fb.group({
            name: [data.name || '', Validators.required]
        });
    }

    ngOnInit(): void {
    }

    onSave(): void {
        if (this.roleForm.valid) {
            const role: Role = {
                ...this.data,
                ...this.roleForm.value
            };

            if (role.id) {
                this.roleService.update(role.id, role).subscribe({
                    next: () => this.dialogRef.close(true),
                    error: (err) => {
                        console.error('Error updating role', err);
                        this.errorMessage = err?.error?.message || err?.error || 'Erreur lors de la mise à jour du rôle';
                        this.cdr.detectChanges();
                    }
                });
            } else {
                this.roleService.create(role).subscribe({
                    next: () => this.dialogRef.close(true),
                    error: (err) => {
                        console.error('Error creating role', err);
                        this.errorMessage = err?.error?.message || err?.error || 'Erreur lors de la création du rôle';
                        this.cdr.detectChanges();
                    }
                });
            }
        }
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onRequestDelete(): void {
        if (!this.data?.id) {
            return;
        }
        this.roleService.canDelete(this.data.id).subscribe({
            next: (canDelete) => {
                this.canDeleteRole = canDelete;
                this.isDeleteConfirmOpen = true;
                this.cdr.detectChanges();
            },
            error: () => {
                this.errorMessage = 'Impossible de vérifier si le rôle peut être supprimé';
                this.cdr.detectChanges();
            }
        });
    }

    confirmDelete(): void {
        if (!this.data?.id) {
            return;
        }
        this.roleService.delete(this.data.id).subscribe({
            next: () => this.dialogRef.close(true),
            error: (err) => {
                this.errorMessage = err?.error?.message || err?.error || 'Erreur lors de la suppression du rôle';
                this.isDeleteConfirmOpen = false;
                this.cdr.detectChanges();
            }
        });
    }

    cancelDelete(): void {
        this.isDeleteConfirmOpen = false;
        this.cdr.detectChanges();
    }

    getDeleteDialogMessage(): string {
        const name = this.data?.name ?? '';
        if (this.canDeleteRole) {
            return `Voulez-vous vraiment supprimer le rôle "${name}" ? Cette action est irréversible.`;
        }
        return `Le rôle "${name}" est attribué à des utilisateurs et ne peut pas être supprimé.`;
    }
}
